import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationService } from '../../services/validation.service';
import { MeterService } from '../../services/meter.service';

@Component({
  selector: 'app-personal-info-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule],
  templateUrl: './personal-info-form.component.html',
  styleUrl: './personal-info-form.component.scss'
})
export class PersonalInfoFormComponent implements OnInit {
  personalInfoForm!: FormGroup;
  isSubmitting = false;
  isSuccess = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private validationService: ValidationService,
    private meterService: MeterService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.setupValueChangeListeners();
    
    // Check if we have eligible meters
    this.meterService.currentMeters.subscribe((meters) => {
      if (Object.keys(meters).length === 0) {
        // No meters selected, redirect back
        this.router.navigate(['/input-meters']);
      }
    });
  }

  initForm(): void {
    this.personalInfoForm = this.fb.group({
      name: ['', [
        Validators.required, 
        Validators.pattern(/^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]+$/)
      ]],
      surname: ['', [
        Validators.required, 
        Validators.pattern(/^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]+$/)
      ]],
      // fatherName: ['', [
      //   Validators.required, 
      //   Validators.pattern(/^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]+$/)
      // ]],
      email: ['', [
        Validators.required, 
        Validators.email
      ]],
      mobileNumber: ['', [
        Validators.required, 
        Validators.pattern(/^[0-9]+$/),
        Validators.maxLength(10)
      ]]
    });
  }

  // Set up value change listeners to convert text to uppercase
  setupValueChangeListeners(): void {
    // For name field
    this.personalInfoForm.get('name')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        this.personalInfoForm.get('name')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    // For surname field
    this.personalInfoForm.get('surname')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        this.personalInfoForm.get('surname')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    // For fatherName field
    // this.personalInfoForm.get('fatherName')?.valueChanges.subscribe(value => {
    //   if (value && typeof value === 'string') {
    //     this.personalInfoForm.get('fatherName')?.setValue(value.toUpperCase(), { emitEvent: false });
    //   }
    // });
  }

  get formControls() {
    return this.personalInfoForm.controls;
  }

  // Convert input to uppercase immediately on input
  convertToUppercase(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    this.personalInfoForm.get(controlName)?.setValue(input.value);
  }

  // Key handling for text fields (letters only)
  onKeyDownLettersOnly(event: KeyboardEvent): boolean {
    // Allow navigation, deletion, etc.
    if (this.isControlKey(event)) {
      return true;
    }

    // Regular expression for allowed characters (letters and spaces)
    // For both English and Greek alphabet
    const regex = /^[A-Za-zΑ-Ωα-ωίϊΐόάέύϋΰήώ\s]$/;
    
    // Test if the pressed key matches the pattern
    if (!regex.test(event.key)) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Key handling for mobile number (digits only)
  onKeyDownDigitsOnly(event: KeyboardEvent): boolean {
    // Allow navigation, deletion, etc.
    if (this.isControlKey(event)) {
      return true;
    }

    // Check if the key is a digit
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Helper method to check if a key is a control key
  private isControlKey(event: KeyboardEvent): boolean {
    // Control keys: navigation, deletion, etc.
    return event.key === 'Backspace' || 
           event.key === 'Delete' || 
           event.key === 'Tab' || 
           event.key === 'Escape' || 
           event.key === 'Enter' || 
           event.key === 'Home' || 
           event.key === 'End' || 
           event.key === 'ArrowLeft' || 
           event.key === 'ArrowRight' || 
           event.key === 'ArrowUp' || 
           event.key === 'ArrowDown' || 
           (event.ctrlKey === true) || // Allow Ctrl+C, Ctrl+V, etc.
           (event.metaKey === true);   // Allow Command key on Mac
  }

  onSubmit(): void {
    if (this.personalInfoForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.formControls).forEach(key => {
        const control = this.formControls[key as keyof typeof this.formControls];
        control.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    // Prepare the data for submission
    const personalInfo = {
      first_name: this.personalInfoForm.get('name')?.value,
      last_name: this.personalInfoForm.get('surname')?.value,
      mobile_number: this.personalInfoForm.get('mobileNumber')?.value,
      email: this.personalInfoForm.get('email')?.value
    };

    // Call the service to submit the data
    this.validationService.submitPersonalInfo(personalInfo).subscribe(
      (response) => {
        this.isSubmitting = false;
        
        // Check if submission was successful
        if (response && response.success !== false) {
          this.isSuccess = true;
          console.log('Personal info submitted successfully:', response);
        } else {
          // Handle error
          this.errorMessage = response.error?.message || 'Error submitting your information. Please try again.';
          console.error('Error submitting personal info:', response);
        }
      },
      (error) => {
        this.isSubmitting = false;
        this.errorMessage = 'An unexpected error occurred. Please try again later.';
        console.error('Unhandled error in personal info submission:', error);
      }
    );
  }

  resetForm(): void {
    this.isSuccess = false;
    this.errorMessage = '';
    this.personalInfoForm.reset();
    Object.keys(this.formControls).forEach(key => {
      const control = this.formControls[key as keyof typeof this.formControls];
      control.setErrors(null);
    });
  }
}